/**
 * Sanitizes workspace names by allowing only safe characters
 * This approach is safer than removing specific characters
 * Can be used both client-side and server-side
 */

const WORKSPACE_NAME_MAX_LENGTH = 100;
const WORKSPACE_NAME_MIN_LENGTH = 1;

/**
 * Sanitizes a workspace name by:
 * 1. Converting to string and trimming whitespace
 * 2. Allowing only alphanumeric characters, spaces, hyphens, and plus signs
 * 3. Limiting length to prevent issues
 * 4. Providing fallback for empty results
 *
 * @param {string} name - The workspace name to sanitize
 * @param {string} fallback - Fallback name if sanitization results in empty string
 * @returns {string} - The sanitized workspace name
 */
function sanitizeWorkspaceName(name, fallback = "New Workspace") {
    if (!name) {
        return fallback;
    }

    // Convert to string and trim
    let sanitized = String(name).trim();

    // Allow only alphanumeric characters, spaces, hyphens, single quotes, and plus signs
    // This is consistent with the client-side pattern but safer than removing specific chars
    sanitized = sanitized.replace(/[^a-zA-Z0-9 +\-']/g, "");

    // Limit length
    sanitized = sanitized.substring(0, WORKSPACE_NAME_MAX_LENGTH);

    // Trim again in case we truncated trailing spaces
    sanitized = sanitized.trim();

    // Return fallback if empty after sanitization
    if (sanitized.length < WORKSPACE_NAME_MIN_LENGTH) {
        return fallback;
    }

    return sanitized;
}

/**
 * Validates if a workspace name is acceptable
 * @param {string} name - The workspace name to validate
 * @returns {boolean} - Whether the name is valid
 */
function isValidWorkspaceName(name) {
    if (!name || typeof name !== "string") {
        return false;
    }

    const trimmed = name.trim();

    // Check length
    if (trimmed.length < WORKSPACE_NAME_MIN_LENGTH || trimmed.length > WORKSPACE_NAME_MAX_LENGTH) {
        return false;
    }

    // Check for valid characters only
    return /^[a-zA-Z0-9 +\-]+$/.test(trimmed);
}

module.exports = {
    sanitizeWorkspaceName,
    isValidWorkspaceName,
    WORKSPACE_NAME_MAX_LENGTH,
    WORKSPACE_NAME_MIN_LENGTH
};
